import React, { useEffect, useState, useMemo } from 'react';
import { useGetEventAttendees } from '../../hooks/useAttendee';
import { EventStatusConstants } from '../../constants/eventStatus';
import { useUpdateEventStatus } from '../../hooks/useEvent';

// Memoize the status lookup function
const getStatusFromText = (statusText) => {
  return EventStatusConstants[Object.keys(EventStatusConstants).find(key =>
    EventStatusConstants[key].text === statusText
  )];
};

export default function EventStatusBadge({status, size = 'xs'}) {
  const statusKey = Object.entries(EventStatusConstants).find(([_, value]) => value.text === status)?.[0] || 'PENDING';
  const statusConfig = EventStatusConstants[statusKey];

  return (
    <span className={`px-3 py-2 rounded-md text-${size} font-semibold ${statusConfig.bgColor} ${statusConfig.textColor}`}>
      {statusConfig.text}
    </span>
  );
}

// Memoize the getEventStatus function to prevent unnecessary recalculations
export const getEventStatus = (startDate, endDate, attendees = [], spend_value = 0, isOrganization = false, tov_export) => {
  const currentDate = new Date();

  // Set start date to beginning of day (00:00:00)
  const eventStart = new Date(startDate);
  eventStart.setHours(0, 0, 0, 0);

  // Set end date to end of day (23:59:59)
  const eventEnd = endDate ? new Date(endDate) : null;
  if (eventEnd) {
    eventEnd.setHours(23, 59, 59, 999);
  }

  // Memoize the attendee data completeness check
  const isAttendeeDataComplete = attendees.length > 0 && attendees.every(attendee =>
    attendee.email &&
    attendee.national_id &&
    attendee.first_name &&
    attendee.family_name &&
    attendee.specialty &&
    attendee.APO_number &&
    attendee.medical_license
  );


  // Check if event is pending (before start date)
  const isPending = currentDate < eventStart;

  // Check if event is started (between start and end date)
  const isStarted = currentDate >= eventStart && eventEnd && currentDate <= eventEnd;

  // Check if event has ended (after end date)
  const hasEnded = eventEnd && currentDate > eventEnd;

  // Check if event is closed (has ended, data complete, and spend is recorded)
  const isCompleted = (isAttendeeDataComplete || isOrganization) && parseFloat(spend_value) > 0;

  // Check if event is closed (has export TOV)
  const isClosed = hasEnded && (isCompleted || isOrganization) && tov_export;

  // Return the appropriate status based on conditions
  if (isClosed) return EventStatusConstants.CLOSED;
  if (isPending) return EventStatusConstants.PENDING;
  if (hasEnded) return isCompleted ? EventStatusConstants.COMPLETED : EventStatusConstants.ENDED;
  if (isStarted) return EventStatusConstants.STARTED;

  // Default to PENDING if no other conditions are met
  return EventStatusConstants.PENDING;
};

export function EventStatus({
  startDate,
  endDate,
  size,
  eventId,
  spend_value = 0,
  isOrganization = false,
  status: cachedStatus,
  tov_export
}) {
  const { data: attendees = [] } = useGetEventAttendees(eventId);
  const { mutate: updateStatus } = useUpdateEventStatus();
  const [error, setError] = useState(null);

  // Memoize the calculated status to prevent unnecessary recalculations
  const calculatedStatus = useMemo(() => {
    let newStatus = getEventStatus(startDate, endDate, attendees, spend_value, isOrganization, tov_export);
    if (cachedStatus === newStatus.text) {
      return getStatusFromText(cachedStatus);
    }
    return newStatus;
  }, [cachedStatus, startDate, endDate, attendees, spend_value, isOrganization, tov_export]);

  // Memoize the status object to prevent unnecessary object creation
  const status = useMemo(() => {
    return calculatedStatus || EventStatusConstants.PENDING;
  }, [calculatedStatus]);

  useEffect(() => {
    if (!calculatedStatus) return;
    if (cachedStatus === calculatedStatus.text) return;

    updateStatus(
      { eventId, status: calculatedStatus.text },
      {
        onError: (error) => {
          console.error('Failed to update event status:', error);
          setError(error);
        }
      }
    );
  }, [calculatedStatus, cachedStatus, eventId, updateStatus]);

  if (error) {
    return (
      <span className={`px-3 py-2 rounded-md text-${size} font-semibold bg-gray-500 text-white`}>
        Unknown
      </span>
    );
  }

  return (
    <span className={`px-3 py-2 rounded-md text-${size} font-semibold ${status.bgColor} ${status.textColor}`}>
      {status.text}
    </span>
  );
}